import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ToastrService } from 'ngx-toastr';
import { Toast } from '@capacitor/toast';

@Injectable({ providedIn: 'root' })
export class BackButtonService {
  /** Aktif modal/drawer kapatma handler'larının yığını */
  private handlers: (() => boolean)[] = [];
  private backPressedOnce = false;
  private backPressTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private router: Router,
    private location: Location,
    private toastr: ToastrService,
    private ngZone: NgZone
  ) {}

  /**
   * Sadece native platformda
   */
  initialize(): void {
    if (!Capacitor.isNativePlatform()) return;

    App.addListener('backButton', (data) => {
      this.ngZone.run(() => {
        // 1. Yığında kayıtlı bir handler varsa (modal/drawer açık)
        if (this.handlers.length > 0) {
          const handler = this.handlers[this.handlers.length - 1];
          const handled = handler();
          if (handled) {
            this.handlers.pop();
            return;
          }
        }

        // 2. Dashboard'daysa → çift tıklama ile çıkış
        const url = this.router.url;
        const isDashboard = url === '/dashboard' || url.startsWith('/dashboard?');
        if (isDashboard) {
          this.handleExit();
          return;
        }

        // 3. Diğer sayfalarda → tarayıcı geçmişine geri dön
        if (data.canGoBack) {
          this.location.back();
        }
      });
    });
  }

  /**
   * Bir modal/drawer açıldığında çağrılır.
   * @param fn Geri tuşuna basıldığında çalışacak kapatma fonksiyonu.
   *           `true` döndürürse "işlendi" anlamına gelir ve yığından çıkarılır.
   */
  pushHandler(fn: () => boolean): void {
    this.handlers.push(fn);
  }

  /**
   * Bir modal/drawer geri tuşu dışında kapatıldığında (örn. iç butonla)
   * yığını manuel temizlemek için çağrılır.
   */
  popHandler(): void {
    if (this.handlers.length > 0) {
      this.handlers.pop();
    }
  }

  /**
   * Sayfa/bileşen destroy olduğunda tüm stack'i temizlemek için kullanılır.
   * Modal açıkken route değişirse handler'lar stack'te birikmesini önler.
   */
  clearAllHandlers(): void {
    this.handlers = [];
  }

  private async handleExit(): Promise<void> {
    if (this.backPressedOnce) {
      App.exitApp();
    } else {
      this.backPressedOnce = true;
      await Toast.show({
        text: 'Çıkmak için tekrar basın',
        duration: 'short',
        position: 'bottom',
      });
      if (this.backPressTimer !== null) {
        clearTimeout(this.backPressTimer);
      }
      this.backPressTimer = setTimeout(() => {
        this.backPressedOnce = false;
        this.backPressTimer = null;
      }, 2000);
    }
  }
}
